/** ****************** IMPORTS ********************/
import { Grid, Tooltip } from '@material-ui/core';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { DateTimePickerCustom } from '~/src/dash/components/datepicker/datetimepicker-custom';
import { getFutureEndDate } from '~/src/dash/utils/date';
import { usePublicaTranslation } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

/** ****************** INTERFACES ********************/

export interface IDateRangeChangeEvent {
  start: moment.Moment;
  end: moment.Moment;
  dateStart: moment.Moment;
  dateEnd: moment.Moment;
}

export interface IDateRangeProps {
  // Required props
  onChange: ({ start, end }: Partial<IDateRangeChangeEvent>) => void;

  // Optional Props
  start?: moment.Moment;
  end?: moment.Moment;
  disableTime?: boolean;
  timezone?: string;
  showTimezone?: boolean;
  openDirection?: 'up' | 'down';
  enableSelectAll?: boolean;
  enableClearSelection?: boolean;
  enablePresets?: boolean;
  minDate?: moment.Moment; // minimum date the calendar can pick
  maxDate?: moment.Moment; // maximum date the calendar can pick
  maxDateExtended?: number; // maximum date the calendar can pick FROM the today measured in days (today is 1/12, maxdate extended 5 days, end date is 1/17)
  disabled?: boolean; // disables entire calendar
  disabledStart?: boolean; // disables the first (start) calendar
  disabledEnd?: boolean; // disables the second (end) calendar
  helperText?: string;
  error?: boolean;
  smallCalendar?: boolean;

  ContainerProps?: { [key: string]: any };
  timezoneComponent?: React.ReactElement;

  // added to check start and end with useEffect in date-time-range-view
  onChangeValidator?: ({ start, end }: Partial<IDateRangeChangeEvent>) => boolean;
}

/** ****************** MAIN COMPONENT ********************/
export const DateTimeRangeComponent = React.memo(
  ({
    ContainerProps,
    onChange,
    onChangeValidator,
    start,
    end,
    disableTime,
    timezone,
    showTimezone,
    openDirection,
    enableSelectAll,
    enableClearSelection,
    enablePresets,
    minDate,
    maxDate,
    maxDateExtended,
    disabled,
    disabledStart,
    disabledEnd,
    helperText,
    error,
    smallCalendar,
    timezoneComponent,
  }: IDateRangeProps) => {
    const { t } = usePublicaTranslation();
    /** ******** States **********/
    // Date States
    const [dateStart, setDateStart] = useState<moment.Moment | null>(start ? start : null);
    const [dateEnd, setDateEnd] = useState<moment.Moment | null>(end ? end : null);

    // State just to keep track of First Render
    const [firstRender, setFirstRender] = useState<boolean | null>(true);

    /** ******** Use Effects **********/
    useEffect(() => {
      // resets the start and end with clear selection
      if ((enableClearSelection && !start) || !end) {
        setDateStart(start ? start : null);
        setDateEnd(end ? end : null);
      }

      if (start && !start.isSame(dateStart)) {
        setDateStart(start);
      }

      if (end && !end.isSame(dateEnd)) {
        setDateEnd(end);
      }
    }, [start, end]);

    useEffect(() => {
      if ((!firstRender && dateStart) || dateEnd) {
        onChange({
          start: dateStart ? dateStart.clone().tz(timezone || 'UTC', true) : start,
          end: dateEnd ? dateEnd.clone().tz(timezone || 'UTC', true) : end,
        });
        setFirstRender(false);
      }
    }, [timezone]);

    /** ******** HANDLE FUNCTIONS **********/
    const handleDateChange = (type: string, date: moment.Moment) => {
      const changeDateObj = {
        start: type === 'start' ? date : start,
        end: type === 'end' ? date : end,
      };
      if (onChangeValidator && !onChangeValidator(changeDateObj)) {
        return;
      }
      onChange(changeDateObj);
    };

    if (maxDate === undefined && maxDateExtended) {
      maxDate = getFutureEndDate(timezone || 'UTC', maxDateExtended, 'day');
    }

    /** ******** MAIN RENDER RETURN **********/
    return (
      <Grid container {...ContainerProps} alignItems='center'>
        {/* Start Date Picker */}
        <DateTimePickerCustom
          placeholder={t('StartDate')}
          type='start'
          date={dateStart}
          minDate={minDate}
          maxDate={maxDate}
          disableTime={!!disableTime}
          timezone={timezone}
          handleDateChange={handleDateChange}
          enableSelectAll={enableSelectAll}
          enableClearSelection={enableClearSelection}
          enablePresets={enablePresets}
          openDirection={openDirection}
          disabled={disabled || disabledStart}
          helperText={helperText}
          error={error}
          smallCalendar={smallCalendar}
        />
        {/* End Date Picker */}
        <DateTimePickerCustom
          placeholder={t('EndDate')}
          type='end'
          date={dateEnd}
          minDate={dateStart ? dateStart : minDate}
          maxDate={maxDate}
          disableTime={!!disableTime}
          timezone={timezone}
          handleDateChange={handleDateChange}
          enableSelectAll={enableSelectAll}
          enableClearSelection={enableClearSelection}
          enablePresets={enablePresets}
          openDirection={openDirection}
          disabled={disabled || disabledEnd}
          helperText={helperText}
          error={error}
          smallCalendar={smallCalendar}
        />
        {/* Show Timezone */}
        {showTimezone && (
          <Grid item container direction='column' style={{ width: '125px', margin: 0 }} spacing={1}>
            <label style={{ height: '20px' }}>Timezone</label>
            <Tooltip
              title={
                timezone ? (
                  <label>
                    [ {timezone} ]<br />
                    Set at the account level
                  </label>
                ) : (
                  ''
                )
              }
              placement={'bottom-start'}
            >
              <span style={{ flexGrow: 1, flexShrink: 0 }}>
                <input
                  value={timezone}
                  disabled
                  style={{
                    height: '100%',
                    width: '180px',
                    border: `1px solid ${publicaColors.lightGray}`,
                    borderRadius: '2px',
                    marginRight: '10px',
                    fontSize: 'inherit',
                    cursor: 'text',
                    display: 'inline-block',
                  }}
                />
              </span>
            </Tooltip>
          </Grid>
        )}
        {!!timezoneComponent && timezoneComponent}
      </Grid>
    );
  },
);
